import { TopNavigationItemProps } from '@/components/atomic/molecules/navigation/top-nav-items/item/typings';
import SliceNames from '@/state/enum';
import getDropdownSegments from '@/state/selectors/navigation/get-dropdown-segments';
import {
	NavigationActions,
	NavigationState,
} from '@/state/slices/navigation/typings';
import { IStore, SetState } from '@/state/slices/typings';
import { PageModel } from '@adobe/aem-react-editable-components';

const getAnimationDirection = (dropdownId?: string): 'right' | 'top' => {
	const directionMap: Record<string, 'right' | 'top'> = {
		'my-account': 'right',
		'contact-and-help': 'right',
		'preferred-retailer': 'right',
	};

	if (dropdownId && directionMap[dropdownId]) {
		return directionMap[dropdownId];
	}

	return 'top';
};

const getAnimationDuration = (dropdownId?: string): number => {
	const durationMap: Record<string, number> = {
		'my-account': 800,
		'contact-and-help': 800,
		'preferred-retailer': 800,
		builds: 500,
	};

	if (dropdownId && durationMap[dropdownId]) {
		return durationMap[dropdownId];
	}

	return 400;
};

const navigationActionsCreator = (
	set: SetState<IStore>,
	get: () => IStore,
): NavigationActions => {
	return {
		[SliceNames.NAVIGATION]: {
			openNavigation: (
				dropdownSegments: NavigationState[SliceNames.NAVIGATION]['dropdownSegments'],
			) => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							open: true,
							dropdownSegments,
							dropdownId: 'mobile-menu',
							animationDirection: 'top',
							animationDuration: 400,
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/openNavigation`,
						dropdownSegments,
					},
				);
			},
			pushDropdownSegment: (
				segment: string,
				replaceIndex: number,
				dropdownId?: string,
				dropdownVariant?: TopNavigationItemProps['$variant'],
			) => {
				const currentSegments = getDropdownSegments(get());
				let nextSegments: string[] = [...currentSegments];
				if (
					segment !== 'mobileRoot' &&
					currentSegments[0] !== 'mobileRoot'
				) {
					nextSegments = ['mobileRoot', ...nextSegments];
				}

				if (nextSegments[replaceIndex] !== undefined) {
					nextSegments[replaceIndex] = segment;
					nextSegments = [
						...nextSegments.slice(0, replaceIndex),
						segment,
						...nextSegments.slice(replaceIndex + 1),
					];
				} else {
					nextSegments = [...nextSegments, segment];
				}

				let dropdownIdState = {};
				let dropdownVariantState = {};
				if (replaceIndex === 1 && dropdownId) {
					dropdownIdState = { dropdownId };
				}

				if (dropdownVariant) {
					dropdownVariantState = { dropdownVariant };
				}

				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							...dropdownIdState,
							animationDirection:
								getAnimationDirection(dropdownId),
							animationDuration: getAnimationDuration(dropdownId),
							open: true,
							dropdownSegments: nextSegments,
							openSideBarSubPanelIndex: undefined,
							...dropdownVariantState,
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/pushDropdownSegment`,
						segment,
						replaceIndex,
					},
				);
			},
			popDropdownSegment: () => {
				const currentDropdownSegments = getDropdownSegments(get());

				if (currentDropdownSegments.length > 1) {
					const dropdownSegments = getDropdownSegments(get()).slice(
						0,
						-1,
					);

					set(
						(state) => ({
							...state,
							[SliceNames.NAVIGATION]: {
								...state[SliceNames.NAVIGATION],
								dropdownSegments,
							},
						}),
						undefined,
						{
							type: `${SliceNames.NAVIGATION}/popDropdownSegment`,
						},
					);
				}
			},
			closeNavigation: () => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							open: false,
						},
					}),
					undefined,
					{ type: `${SliceNames.NAVIGATION}/closeNavigation` },
				);
			},
			resetDropdownId: () => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							dropdownId: 'mobile-menu',
						},
					}),
					undefined,
					{ type: `${SliceNames.NAVIGATION}/resetDropdownId` },
				);
			},
			setGenericDropdownState: (key: string, value: any) => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							genericDropdownState: {
								...state[SliceNames.NAVIGATION]
									.genericDropdownState,
								[key]: value,
							},
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/setGenericDropdownState`,
						key,
						value,
					},
				);
			},
			resetGenericDropdownState: () => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							genericDropdownState: {},
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/resetGenericDropdownState`,
					},
				);
			},
			addModel: (endpoint: string, model: PageModel) => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							models: {
								...state[SliceNames.NAVIGATION].models,
								[endpoint]: {
									...model,
									isPrimary:
										Object.keys(
											state[SliceNames.NAVIGATION].models,
										).length === 0,
								},
							},
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/addModel`,
						endpoint,
						model,
					},
				);
			},
			openSideBarSubPanel: (index: number) => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							openSideBarSubPanelIndex: index,
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/openSideBarSubPanel`,
						index,
					},
				);
			},
			closeSideBarSubPanel: () => {
				set(
					(state) => ({
						...state,
						[SliceNames.NAVIGATION]: {
							...state[SliceNames.NAVIGATION],
							openSideBarSubPanelIndex: undefined,
						},
					}),
					undefined,
					{
						type: `${SliceNames.NAVIGATION}/closeSideBarSubPanel`,
					},
				);
			},
		},
	};
};

export default navigationActionsCreator;
